import React, { Component } from 'react'
import { Field, FieldArray, reduxForm, formValueSelector } from 'redux-form'
import { connect } from 'react-redux'
import Select from 'react-select'

import {
  AutoCompleteField
} from '../common/FormFields'

// Ids of standards already picked by OTHER veto slots (mutual exclusivity:
// a standard belongs to exactly one Veto Person).
const usedByOthers = (vetoValues, index) => {
  const used = []
  ;(vetoValues || []).forEach((row, j) => {
    // Only rows with a selected user count — a standard on a user-less row is
    // dropped at submit, so it must not reserve the standard from other slots.
    if (j === index || !row || !row.user_id) return
    ;(row.standard || []).forEach((s) => used.push(s.value))
  })
  return used
}

// Diff of the current selection vs the stored assignment (AC-2): who is being
// added / removed and whose standards changed, so the confirmation dialog can
// summarise the edit before it is applied.
const computeAssignmentChanges = (initialVeto, vetoValues) => {
  const before = (initialVeto || []).filter((r) => r && r.user_id)
  const after = (vetoValues || []).filter((r) => r && r.user_id)
  const idOf = (r) =>
    String(r.user_id.value && r.user_id.value.id != null ? r.user_id.value.id : r.user_id.value)
  const nameOf = (r) => r.user_id.label
  const stdsOf = (r) => (r.standard || []).map((s) => String(s.value)).sort().join('|')

  const beforeIds = before.map(idOf)
  const afterIds = after.map(idOf)
  const added = after.filter((r) => beforeIds.indexOf(idOf(r)) === -1).map(nameOf)
  const removed = before.filter((r) => afterIds.indexOf(idOf(r)) === -1).map(nameOf)
  const standardsChanged = after
    .filter((r) => {
      const prev = before.find((b) => idOf(b) === idOf(r))
      return prev && stdsOf(prev) !== stdsOf(r)
    })
    .map(nameOf)

  return {
    added,
    removed,
    standardsChanged,
    isEdit: before.length > 0,
    hasChanges: added.length + removed.length + standardsChanged.length > 0
  }
}

// Standards from the order that no (user-assigned) veto slot has assigned yet.
const missingStandards = (vetoValues, standardOptions) => {
  const assigned = []
  ;(vetoValues || []).forEach((row) => {
    if (!row || !row.user_id) return
    ;(row.standard || []).forEach((s) => assigned.push(s.value))
  })
  return (standardOptions || []).filter((o) => assigned.indexOf(o.value) === -1)
}

// redux-form Field wrapper around a react-select multi for standards.
// NOTE: do NOT wire onBlur to input.onBlur(input.value). react-select fires
// onBlur right after a selection, and the closure captures the PREVIOUS
// render's input.value — committing it would clobber the just-added option
// (dropping the 2nd+ standard). Leaving onBlur unwired keeps every selection.
const StandardMultiSelect = ({ input, options, placeholder }) => (
  <Select
    multi
    value={input.value || []}
    options={options}
    placeholder={placeholder || 'Select standard(s)…'}
    onChange={(val) => input.onChange(val)}
  />
)

const RenderVetos = ({ fields, standardOptions, vetoValues, userAPI, meta: { error, submitFailed } }) => {
  return (
    <div className='form-group veto'>
      <label className='col-sm-3 control-label'>Veto Person</label>
      <div className='col-sm-9'>

        {fields.map((veto, index) => {
          const taken = usedByOthers(vetoValues, index)
          const optionsForSlot = (standardOptions || []).filter(
            (o) => taken.indexOf(o.value) === -1
          )
          return (
            <div key={index}>
              <Field
                name={`${veto}.user_id`}
                component={AutoCompleteField}
                label='User'
                api={userAPI}
              />

              <div className='form-group'>
                <label className='col-sm-3 control-label'>
                  Standard <small className='text-muted'>(optional)</small>
                </label>
                <div className='col-sm-9'>
                  <Field
                    name={`${veto}.standard`}
                    component={StandardMultiSelect}
                    options={optionsForSlot}
                  />
                </div>
              </div>

              <div className='row'>
                <div className='col-md-8 col-md-push-4'>
                  <button
                    className='btn btn-sm pull-right'
                    type='button'
                    onClick={() => fields.remove(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <hr />
            </div>
          )
        })}

        <button className='btn btn-default btn-sm' type='button' onClick={() => fields.push({})}>Add User</button>
        {submitFailed && error && <span className='text-danger'>{error}</span>}
      </div>
    </div>
  )
}

export class VetoForm extends Component {
  constructor (props) {
    super(props)
    this.state = { showConfirm: false, pendingValues: null }
  }

  openConfirm (values) {
    this.setState({ showConfirm: true, pendingValues: values })
  }

  closeConfirm () {
    // Cancel returns to the pop-up with everything retained; nothing is saved.
    // Ignored while the submit is in flight (the request cannot be cancelled).
    if (this.props.isSubmitting) return
    this.setState({ showConfirm: false, pendingValues: null })
  }

  confirmSubmit () {
    // The confirmation dialog stays OPEN while the request runs (the server
    // also sends the notification emails in the same request, which can take
    // several seconds). It shows a busy state until the container either
    // closes the whole pop-up on success or hands back an error message.
    const values = this.state.pendingValues
    this.props.onSubmit({ ...values, advance: true })
  }

  render () {
    const { handleSubmit, submitting, standardOptions, vetoValues, isInPlaceEdit, initialVeto, isSubmitting, submitErrorMessage } = this.props
    const missing = missingStandards(vetoValues, standardOptions)
    const submitDisabled = submitting || missing.length > 0
    const picked = (vetoValues || []).filter((r) => r && r.user_id)
    const changes = computeAssignmentChanges(initialVeto, vetoValues)

    return (
      <div>
        {/* react-select v1 gives an open menu a low z-index, so the Standard
            field below covers the open User menu. Lift whichever select is open
            (and its menu) above the following fields. */}
        <style>{`
          .veto-form .Select.is-open { position: relative; z-index: 1000; }
          .veto-form .Select-menu-outer { z-index: 1000; }
        `}</style>
        <form className='form-horizontal veto-form' onSubmit={handleSubmit}>

          {missing.length > 0 && (
            <div className='alert alert-warning' style={{ marginBottom: 15 }}>
              Not all order standards are assigned yet:{' '}
              <b>{missing.map((s) => s.label).join(', ')}</b>. Submit is disabled
              until every standard is assigned; Save (draft) is still allowed.
            </div>
          )}

          <FieldArray
            name='veto'
            component={RenderVetos}
            standardOptions={standardOptions}
            vetoValues={vetoValues}
            userAPI={this.props.userAPI}
          />

          <p className='help-text col-sm-offset-3 col-sm-9' style={{ marginBottom: 15 }}>
            Standard assignment is optional per Veto Person, but every order
            standard must be assigned to exactly one Veto Person before the veto
            review can be submitted.
          </p>

          <div className='form-group'>
            {/* In-place edit (order already at Veto Person Assigned): the active
                assignment must stay complete, so there is no partial Save —
                only Submit with its confirmation (AC-2). */}
            {!isInPlaceEdit && (
              <div className='col-sm-offset-3 col-sm-6'>
                <button disabled={submitting} type='submit' className='btn btn-default btn-block'>Save</button>
              </div>
            )}
            <div className={isInPlaceEdit ? 'col-sm-offset-3 col-sm-9' : 'col-sm-3'}>
              <button
                disabled={submitDisabled}
                type='button'
                className='btn btn-primary btn-block'
                onClick={handleSubmit((values) => this.openConfirm(values))}
              >
                Submit
              </button>
            </div>
          </div>

        </form>

        {this.state.showConfirm && (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(20,28,38,.45)', zIndex: 1080,
              display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
              padding: '40px 16px', overflow: 'auto'
            }}
          >
            <div className='panel panel-default' style={{ width: '100%', maxWidth: 520, marginTop: 20 }}>
              <div className='panel-heading'><b>Confirm Veto Person Assignment</b></div>
              <div className='panel-body'>
                <p>
                  Please review before submitting. The assigned Veto Persons will
                  be notified, and the veto review form sections will follow this
                  assignment.
                </p>
                {changes.isEdit && changes.hasChanges && (
                  <div style={{ marginBottom: 12 }}>
                    {changes.removed.map((n) => (
                      <div key={`r-${n}`} className='text-danger'>
                        <b>−</b> Removed: <b>{n}</b>
                      </div>
                    ))}
                    {changes.added.map((n) => (
                      <div key={`a-${n}`} className='text-success'>
                        <b>+</b> Added: <b>{n}</b>
                      </div>
                    ))}
                    {changes.standardsChanged.map((n) => (
                      <div key={`m-${n}`} style={{ color: '#194f9c' }}>
                        <b>⇄</b> Standards changed for <b>{n}</b>
                      </div>
                    ))}
                  </div>
                )}
                {missing.length > 0 && (
                  <div className='alert alert-warning'>
                    Standard not yet assigned: <b>{missing.map((s) => s.label).join(', ')}</b>.
                    The veto review cannot be submitted until every standard has an owner.
                  </div>
                )}
                <table className='table table-bordered' style={{ marginBottom: 0 }}>
                  <thead>
                    <tr><th>Veto Person</th><th>Standard(s)</th></tr>
                  </thead>
                  <tbody>
                    {picked.length > 0 ? picked.map((r, i) => (
                      <tr key={i}>
                        <td>{r.user_id && r.user_id.label}</td>
                        <td>
                          {(r.standard && r.standard.length)
                            ? r.standard.map((s) => (
                              <span key={s.value} className='label label-success' style={{ marginRight: 4 }}>{s.label}</span>
                            ))
                            : <span className='text-muted'>no standard assigned</span>}
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={2} className='text-muted'>No Veto Person selected</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {isSubmitting && (
                <div className='panel-body' style={{ borderTop: '1px solid #ddd', paddingTop: 12, paddingBottom: 12 }}>
                  <i className='fa fa-spinner fa-spin' />{' '}
                  Submitting assignment and notifying the Veto Persons… please keep this window open.
                </div>
              )}
              {!isSubmitting && submitErrorMessage && (
                <div className='panel-body' style={{ borderTop: '1px solid #ddd', paddingTop: 12, paddingBottom: 0 }}>
                  <div className='alert alert-danger'>
                    <b>Submit failed.</b> {submitErrorMessage}
                  </div>
                </div>
              )}
              <div className='panel-footer text-right'>
                <button
                  type='button'
                  className='btn btn-default'
                  disabled={isSubmitting}
                  onClick={() => this.closeConfirm()}
                >
                  Cancel
                </button>
                {' '}
                <button
                  type='button'
                  className='btn btn-primary'
                  disabled={isSubmitting || missing.length > 0}
                  onClick={() => this.confirmSubmit()}
                >
                  {isSubmitting
                    ? <span><i className='fa fa-spinner fa-spin' /> Submitting…</span>
                    : submitErrorMessage ? 'Retry Submit' : 'Confirm & Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }
}

VetoForm = reduxForm({ // eslint-disable-line
  form: 'vetoForm'
})(VetoForm)

const selector = formValueSelector('vetoForm')
VetoForm = connect((state) => ({ // eslint-disable-line
  vetoValues: selector(state, 'veto') || []
}))(VetoForm)

export default VetoForm